import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QuickNote, User } from '@xmedical/core';
import { XmedicalUiModule } from '@xmedical/ui';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// Pure I/O quick-note. The container supplies the current user (via input
// rather than CoreService injection) so the component can stamp
// lastModifiedUser without depending on app-side services.
@Component({
  selector: 'xcs-quick-note',
  templateUrl: './quick-note.component.html',
  styleUrls: ['./quick-note.component.scss'],
  imports: [CommonModule, FormsModule, XmedicalUiModule],
})
export class QuickNoteComponent implements OnInit, OnDestroy {

  private debounceTimeOut = 750;
  private noteChangedSubscription: Subscription | null = null;
  // `note` stays a decorator-based @Input because the parent passes a shared
  // QuickNote reference that this component mutates in place via [(ngModel)]
  // and lastModifiedDate/User stamping. A read-only signal input would
  // require restructuring the ngModel bindings and the ngOnInit "default
  // when null/empty" path. Leaving as @Input is intentional.
  @Input() note: QuickNote | null = null;
  readonly loading = input(false);
  /** Current user — used to stamp createdUser / lastModifiedUser. */
  readonly currentUser = input.required<User>();
  readonly noteChange = output<QuickNote>();
  noteTextChanged: Subject<string> = new Subject();

  ngOnInit(): void {
    if (!this.note || this.note.id === 0) {
      this.note = this.newNote();
    }
    this.noteChangedSubscription = this.noteTextChanged.pipe(
      debounceTime(this.debounceTimeOut),
      distinctUntilChanged(),
    ).subscribe((value) => {
      if (!this.note) return;
      this.note.note = value;
      this.note.lastModifiedDate = new Date();
      this.note.modifiedDateUtc = new Date();
      this.note.lastModifiedUser = this.currentUser();
      this.noteChange.emit(this.note);
    });
  }

  ngOnDestroy(): void {
    this.noteChangedSubscription?.unsubscribe();
  }

  get hasNote(): boolean {
    return this.note !== undefined && this.note !== null;
  }

  private newNote(): QuickNote {
    return {
      note: '',
      createdDate: new Date(),
      lastModifiedDate: new Date(),
      createdDateUtc: new Date(),
      modifiedDateUtc: new Date(),
      createdUser: this.currentUser(),
      lastModifiedUser: this.currentUser(),
    } as QuickNote;
  }
}
